/**
 * A mock PRISM clip and PRISM's OGraf mapping (docs/prism-handover.md, "The OGraf mapping (per clip)"), shared by
 * the editor's Preview panel (timeline mode), the api's save-time check (thumbnail at the step-0 hold) and the tests.
 * Pure and browser-safe.
 *
 *   timestamp(n) = n × 1000 × den / num        (one division per value; never a sum of frame durations)
 *
 *   load(non-realtime) → setActionsSchedule([
 *     timestamp(0)                    playAction { goto: 0 }
 *     each step mark s                playAction { delta: 1 }
 *     each update u                   updateAction { data: clip data + every update up to u }
 *     timestamp((D − 1) − ceil(stopMs·num/(1000·den)))   stopAction {}     (clamped ≥ 0; omitted for stepCount 0)
 *   ]) → goToTime(timestamp(n)) for every frame 0 ≤ n < D.
 *
 * The out animation completes on the clip's last rendered frame (D − 1), which is fully transparent (ADR 0028).
 */
import { frameToMs, type FrameRate } from '@prism-graphics/scene-schema/time';

export type { FrameRate } from '@prism-graphics/scene-schema/time';

type Manifest = Record<string, unknown>;

export interface ClipUpdate {
  /** Frame index from the clip start. */
  frame: number;
  /** Partial data merged on top of the clip data (and earlier updates). */
  data: Record<string, unknown>;
}

export interface Clip {
  /** Clip length D in frames. */
  frames: number;
  /** Frame indices of the step marks (each is playAction { delta: 1 }). */
  stepMarks: number[];
  updates: ClipUpdate[];
}

export interface ScheduleEntry {
  timestamp: number;
  action: { type: 'playAction' | 'updateAction' | 'stopAction'; params: Record<string, unknown> };
}

/** PRISM's default hold for a new clip (GET /api/v1/graphics/settings → defaultHoldSeconds). */
export const DEFAULT_HOLD_SECONDS = 4;

/** The start of frame n in ms (rational; exact for any n). */
export const timestamp = (n: number, rate: FrameRate): number => frameToMs(n, rate);

/** Frames needed to cover `ms` (ceil, robust to float error). */
export function framesFor(ms: number, rate: FrameRate): number {
  const exact = (ms * rate.num) / (1000 * rate.den);
  const nearest = Math.round(exact);
  return Math.abs(exact - nearest) < 1e-6 ? nearest : Math.ceil(exact);
}

/** The graphic's frame rate: v_editor.frameRate (rational) when present, else renderRequirements' ideal fps. */
export function manifestFrameRate(manifest: Manifest): FrameRate {
  const v = (manifest.v_editor as { frameRate?: FrameRate } | undefined)?.frameRate;
  if (v && Number.isInteger(v.num) && Number.isInteger(v.den) && v.num > 0 && v.den > 0) return { num: v.num, den: v.den };
  const ideal = (manifest.renderRequirements as Array<{ frameRate?: { ideal?: number; exact?: number } }> | undefined)?.[0]?.frameRate;
  const f = ideal?.exact ?? ideal?.ideal;
  if (typeof f === 'number' && f > 0) {
    const ntsc = (f * 1001) / 1000;
    if (Math.abs(ntsc - Math.round(ntsc)) < 1e-3 && Math.abs(f - Math.round(f)) > 1e-3) return { num: Math.round(ntsc) * 1000, den: 1001 };
    if (Math.abs(f - Math.round(f)) < 1e-6) return { num: Math.round(f), den: 1 };
    return { num: Math.round(f * 1000), den: 1000 };
  }
  return { num: 50, den: 1 };
}

export function stepCountOf(manifest: Manifest): number {
  return typeof manifest.stepCount === 'number' ? manifest.stepCount : 1;
}

/** An action's duration in ms from actionDurations; missing or -1 counts as 0. For playAction, `step` picks steps[]. */
export function actionDuration(manifest: Manifest, type: string, step?: number): number {
  const list = (manifest.actionDurations as Array<{ type: string; duration?: number; steps?: Array<{ step: number; duration: number }> }> | undefined) ?? [];
  const d = list.find((a) => a.type === type);
  if (!d) return 0;
  if (step !== undefined) {
    const s = d.steps?.find((x) => x.step === step);
    if (s) return s.duration > 0 ? s.duration : 0;
  }
  return typeof d.duration === 'number' && d.duration > 0 ? d.duration : 0;
}

/**
 * A default clip length in frames, as PRISM snaps it to the sequence's frame grid (msToFrameTicks): one division,
 * rounded half up, at least one frame. 5100 ms at 25 fps is 127.5 → 128 frames; 5010 ms is 125.25 → 125.
 */
export function lengthFrames(ms: number, rate: FrameRate): number {
  return Math.max(1, Math.round((ms * rate.num) / (1000 * rate.den)));
}

/**
 * The default clip when a graphic is dropped on PRISM's timeline: in + hold + out, snapped with lengthFrames (PRISM's
 * rule, rounded half up). For stepCount 0 the length is the graphic's own (its playAction). For several steps the preview spreads the step marks over the hold (PRISM starts
 * with none; the operator adds them).
 */
export function defaultClip(manifest: Manifest, rate: FrameRate, holdSeconds = DEFAULT_HOLD_SECONDS): Clip {
  const steps = stepCountOf(manifest);
  if (steps === 0) return { frames: lengthFrames(actionDuration(manifest, 'playAction') || Math.round(holdSeconds * 1000), rate), stepMarks: [], updates: [] };
  const inMs = actionDuration(manifest, 'playAction', 0);
  const outMs = actionDuration(manifest, 'stopAction');
  const hold = Math.round(holdSeconds * 1000);
  const frames = lengthFrames(inMs + hold + outMs, rate);
  const stepMarks: number[] = [];
  for (let k = 1; k < steps; k++) stepMarks.push(framesFor(inMs + (hold * k) / steps, rate));
  return { frames, stepMarks, updates: [] };
}

/**
 * Frame index of the stopAction: (D − 1) − out frames, clamped ≥ 0, so the out animation completes on the clip's last
 * rendered frame (D − 1) and that frame is fully transparent (ADR 0028). Undefined for stepCount 0.
 */
export function stopFrame(manifest: Manifest, clip: Clip, rate: FrameRate): number | undefined {
  if (stepCountOf(manifest) === 0) return undefined;
  return Math.max(0, clip.frames - 1 - framesFor(actionDuration(manifest, 'stopAction'), rate));
}

/** PRISM's setActionsSchedule for a clip (ascending; ties keep the table's order). */
export function clipSchedule(manifest: Manifest, clip: Clip, rate: FrameRate, data: Record<string, unknown> = {}): ScheduleEntry[] {
  const entries: Array<ScheduleEntry & { order: number; frame: number }> = [];
  let order = 0;
  entries.push({ frame: 0, order: order++, timestamp: timestamp(0, rate), action: { type: 'playAction', params: { goto: 0 } } });
  for (const s of [...clip.stepMarks].sort((a, b) => a - b)) entries.push({ frame: s, order: order++, timestamp: timestamp(s, rate), action: { type: 'playAction', params: { delta: 1 } } });
  let merged = { ...data };
  for (const u of [...clip.updates].sort((a, b) => a.frame - b.frame)) {
    merged = { ...merged, ...u.data };
    entries.push({ frame: u.frame, order: order++, timestamp: timestamp(u.frame, rate), action: { type: 'updateAction', params: { data: { ...merged } } } });
  }
  const stop = stopFrame(manifest, clip, rate);
  if (stop !== undefined) entries.push({ frame: stop, order: order++, timestamp: timestamp(stop, rate), action: { type: 'stopAction', params: {} } });
  entries.sort((a, b) => a.frame - b.frame || a.order - b.order);
  return entries.map(({ timestamp: t, action }) => ({ timestamp: t, action }));
}

/**
 * The poster / thumbnail time: the step-0 hold, i.e. the end of the in animation (playAction steps[0], else its
 * duration) plus one frame; 1000 ms when that duration is missing or -1. For stepCount 0, half the graphic's length.
 */
export function posterTimestamp(manifest: Manifest, rate: FrameRate): number {
  const list = (manifest.actionDurations as Array<{ type: string; duration?: number; steps?: Array<{ step: number; duration: number }> }> | undefined) ?? [];
  const play = list.find((a) => a.type === 'playAction');
  if (stepCountOf(manifest) === 0) {
    const d = play?.duration;
    return typeof d === 'number' && d > 0 ? d / 2 : 1000;
  }
  const d = play?.steps?.find((s) => s.step === 0)?.duration ?? play?.duration;
  if (typeof d !== 'number' || d < 0) return 1000;
  return timestamp(framesFor(d, rate) + 1, rate);
}
